import { Injectable } from '@angular/core';
import notify from 'devextreme/ui/notify';
import { HttpClient } from '@angular/common/http';
import { SharedService } from '../shared.service';

@Injectable()
export class UsefulToolsService {
  constructor(
    private httpclient: HttpClient,
    private sharedservice: SharedService
  ) { }

  /**
   * Used to check if a http response has the correct properties (reponse.utLogin.result...)
   * @param pCall - the exact url used for the call ('http://192.160.0.144:88/utLogin?SessionID=HWJD...')
   * @param pResponse - the response object from the call
   * @param pProperties - the properties to check in the reponse (eg. ['utLogin_response', 'queue'])
   * @example if (!this.usefultoolsservice.isValidResponse(url, response, ['utPrepareSQL_response', 'RESPONSE'])) return;
  */
  isValidResponse(pCall: String, pResponse: Object, pProperties: Array<string>) {
    // Check if a response from a http call is valid
    // pCall = the url used to do the call (x/utClient?SessionID=awdawd etc)
    // pResponse = the response from the http call
    // pProperties = the properties to check (we check if the response contains these properties)

    // Build the hasOwnProperty string
    let evalStr = `pResponse.hasOwnProperty('${pProperties[0]}')`;
    if (pProperties.length > 1) {
      pProperties.forEach((element, index) => {
        if (index === 0) return;
        let str = '';
        for (let i = 0; i < index; i++) {
          str += `.${pProperties[i]}`;
        }
        evalStr += ` && pResponse${str}.hasOwnProperty('${element}')`;
      });
    }

    // Check if the object has the correct properties
    if (eval(evalStr)) {
      return true;
    } else {
      notify('Data call failed. Please contact IT if this keeps occurring.', 'error', 3000);
      return false;
    }
  }

  /**
   * ASYNC: use await!
   *
   * Do an API call (httpclient)
   * @param pType - 'get', 'post', 'put', 'delete'
   * @param pURL - the URL of the call, WITHOUT the ip and port (so 'utLogin?SessionID=DAWD...')
   * @param pSendingObj - the sending object if a put or post is done
  */
  async doApiCall(pType, pURL, pSendingObj?): Promise<any> {
    // Not sure if this will cause problems:
    // pURL = pURL.replace(/\s/g, '');
    // it is used to replace the spaces that `` create, otherwise the calls fail
    // yes this causes problems, don't ever use it!

    if (pURL[0] != '/') pURL = '/' + pURL;

    let result: any;
    await new Promise(resolve => {
      if (pSendingObj) {
        this.httpclient[pType](`${this.sharedservice.URL}${pURL}`, pSendingObj).subscribe((response: any) => {
          result = response;
          resolve();
        });
      } else {
        this.httpclient[pType](`${this.sharedservice.URL}${pURL}`).subscribe((response: any) => {
          result = response;
          resolve();
        });
      }
    }); // await end

    return result;
  }

  /** Flatten a template string (\`\`) with or without spaces in the breaks */
  flattenTemplateString(pString: String, pSpacesBetweenLines: Boolean) {
    // pSpaceBetweenLines = true:
    // const str = `
    //   my name
    //   is not
    //   peter
    // `;
    // becomes
    // const str = 'my name is not peter';
    // pSpaceBetweenLines = false:
    // const str = `
    //   my name
    //   is not
    //   peter
    // `;
    // becomes
    // const str = 'my nameis notpeter';
    return pSpacesBetweenLines ? pString.replace(/\n+/gi, '').replace(/\s+/gi, ' ') : pString.replace(/\s*\n\s*/g, '');
  }

  /**
   * Call DevExtreme's notify, with an automatic timing
   * @param pType - 'error', 'warning', 'success', 'info'
   * @param pMessage - the message to display to the user
  */
  notify(pType: string, pMessage: string) {
    let duration = pMessage.length * 80;
    if (duration < 3000) duration = 3000;
    notify(pMessage, pType, duration);
  }

  /**
   * Converts a Date (eg. new Date()) to YYYY-MM-DD or YYYY-MM-DD HH:MM:SS.ssss
   * @param pWithoutTime - true: date only, false: date with time
  */
  convertDate(pDate, pWithoutTime: Boolean) {
    if (typeof pDate === 'string') pDate = new Date(pDate);
    // Returns date string in format "CCYY-MM-DD" OR "CCYY-MM-DD HH:MM:SS.ssss"
    const sY = pDate.getFullYear().toString();
    let sM = (pDate.getMonth() < 9 ? '0' : '') + (pDate.getMonth() + 1).toString();
    const sD = (pDate.getDate() < 10 ? '0' : '') + pDate.getDate().toString();
    let ret = sY + '-' + sM + '-' + sD;
    if (!(pWithoutTime || false)) {
      const sH = (pDate.getHours() < 10 ? '0' : '') + pDate.getHours().toString();
      sM = (pDate.getMinutes() < 10 ? '0' : '') + pDate.getMinutes().toString();
      const s = pDate.getSeconds();
      const sS = (s < 10 ? '0' : '') + s.toString();
      const ms = pDate.getMilliseconds().toString();
      const sMS = (ms.length == 1 ? '00' : (ms.length == 2 ? '0' : '')) + ms;
      ret = ret + ' ' + sH + ':' + sM + ':' + sS + '.' + sMS;
    }
    return ret;
  }

  /** Converts a Date (eg. new Date()) to YYYY-MM-DD */
  justDate(pDate: Date) {
    return this.convertDate(pDate, true);
  }

  /** Converts a Date (eg. new Date()) to a time (HH:MM) */
  justTime(pDate: Date) {
    return (this.convertDate(pDate, false)).substr(11, 5);
  }

  /**
   * ASYNC: use await!
   *
   * Rotate a Base64 image (a string) 'left' or 'right'
   * @param pBase64Image - the base64 image as a string
   * @param pLeftorRight - 'left' or 'right'
  */
  async rotateBase64(pBase64Image, pLeftorRight) {
    const image = new Image();
    image.src = pBase64Image;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    return await new Promise<string>(resolve => {
      image.onload = () => {
        canvas.width = image.height;
        canvas.height = image.width;
        if (pLeftorRight == 'left') {
          ctx.rotate(90 * Math.PI / 180);
          ctx.translate(0, -canvas.width);
        }
        if (pLeftorRight == 'right') {
          ctx.rotate(-90 * Math.PI / 180);
          ctx.translate(-canvas.height, 0);
        }
        ctx.drawImage(image, 0, 0);

        resolve(canvas.toDataURL('image/jpeg'));
      };
    });
  }

  /**
   * Resize an image so that its longest side is not larger than the max dimention
   * @param pBase64 - the base64 image as a string
   * @param pMaxDimention - the max dimention of the image (in px)
  */
  async resizeImage(pBase64, pMaxDimention) {
    // Get current image width and height
    let width: any;
    let height: any;
    const i = new Image();
    await new Promise(resolve => {
      i.onload = function () {
        width = i.width;
        height = i.height;
        resolve();
      }
      i.src = pBase64;
    });

    // Resize Max Width & Height (but maintain proportions)
    if (width > height) {
      if (width > pMaxDimention) {
        height *= pMaxDimention / width;
        width = pMaxDimention;
      }
    } else {
      if (height > pMaxDimention) {
        width *= pMaxDimention / height;
        height = pMaxDimention;
      }
    }

    // Create & set the canvas to the new calculated dimensions
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(i, 0, 0, width, height);

    const dataUrl = canvas.toDataURL();
    return dataUrl;
  }

  /**
   * ASYNC: use await!
   *
   * Convert a File to base64 and return the base64 string
   * @param pFile - the base64 image as a string
   * @example
   * async fileUploaded(pEvent) {
   *  const base64Image = await this.usefultoolsservice.fileToBase64(pEvent.target.files[0]);
   * }
   * <label for="uploadPhoto" class="custom-file-upload">Upload Image</label>
   * <input id="uploadPhoto" type="file" (change)="fileUploaded($event)" accept="image/jpeg" hidden/>
  */
  async fileToBase64(pFile) {
    const reader = new FileReader();
    reader.readAsDataURL(pFile);
    return await new Promise<string>(resolve => {
      reader.onload = () => {
        resolve(reader.result.toString());
      };
    });
  }

  /**
      * ASYNC: use await!
      *
      * Convert a File to binary and return the binary string
      * @param pFile - the binary image as a string
      * @example
      * async fileUploaded(pEvent) {
      *  const binaryImage = await this.usefultoolsservice.fileToBinary(pEvent.target.files[0]);
      * }
      * <label for="uploadPhoto" class="custom-file-upload">Upload Image</label>
      * <input id="uploadPhoto" type="file" (change)="fileUploaded($event)" accept="image/jpeg" hidden/>
  */
  async fileToBinary(pFile) {
    const reader = new FileReader();
    reader.readAsDataURL(pFile);
    return await new Promise<string>(resolve => {
      reader.onload = () => {
        resolve(atob(reader.result.toString()));
      };
    });
  }
}
